import React, { useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { useForm, FormProvider } from "react-hook-form";
import RHFSelect from "@/components/form/RHFSelect";
import { step2Schema } from "../validation.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import RHFTextArea from "@/components/form/RHFTextArea";

const Step2 = ({ data, setData, setIsStepValid, scrollRef, setFormMethods }) => {
  const methods = useForm({
    resolver: zodResolver(step2Schema),
    mode: "onChange",
    defaultValues: {
      jdMode: data.jdMode || "improve",
      improvementType: data.improvementType || "",
      responseDepth: data.responseDepth || "",
      customFocus: data.customFocus || "",
      enhancement_tone: data.enhancement_tone || "",
      improvement_flexibility: data.improvement_flexibility || "",
    },
  });

  const {
    watch,
    setValue,
    register,
  } = methods;

  useEffect(() => {
    setFormMethods?.(methods);
  }, [methods, setFormMethods]);

  const allValues = watch();
  const jdMode = allValues.jdMode || "improve";
  const improvementType = allValues.improvementType || "";
  const customFocus = allValues.customFocus || "";
  const responseDepth = allValues.responseDepth || "";
  const enhancementTone = allValues.enhancement_tone || "";
  const improvementFlexibility = allValues.improvement_flexibility || "";

  const hasCustomText = customFocus.trim().length > 0;
  const hasPresetSelected = improvementType.trim().length > 0 && improvementType !== "custom";

  const showDropdown = !hasCustomText;
  const showCustomBox = !hasPresetSelected;

  const getFirstMissingField = (hasFocusOption, currentResponseDepth, currentEnhancementTone, currentImprovementFlexibility) => {
    if (!hasFocusOption) return "focusOption";
    if (!currentResponseDepth) return "responseDepth";
    if (!currentEnhancementTone) return "enhancement_tone";
    if (!currentImprovementFlexibility) return "improvement_flexibility";
    return null;
  };

  const hasFocusOption = hasPresetSelected || hasCustomText;
  const firstMissingField = getFirstMissingField(hasFocusOption, responseDepth, enhancementTone, improvementFlexibility);

  useEffect(() => {
    const subscription = watch((values) => {
      setData((prev) => ({ ...prev, ...values }));
    });
    return () => subscription.unsubscribe();
  }, [watch, setData]);

  useEffect(() => {
    if (!setIsStepValid) return;
    if (jdMode === "extract") {
      setIsStepValid(true);
      return;
    }
    const hasRequiredFocus = (improvementType.trim().length > 0 && improvementType !== "custom") || customFocus.trim().length > 0;
    const hasAllRequired = responseDepth.trim().length > 0 && enhancementTone.trim().length > 0 && improvementFlexibility.trim().length > 0;
    setIsStepValid(hasRequiredFocus && hasAllRequired);
  }, [jdMode, improvementType, customFocus, responseDepth, enhancementTone, improvementFlexibility, setIsStepValid]);

  const customFocusField = register("customFocus");

  const handleCustomChange = (e) => {
    customFocusField.onChange(e);
    const value = e.target.value;
    if (value.trim().length > 0) {
      setValue("improvementType", "custom", { shouldDirty: true, shouldTouch: true, shouldValidate: true });
      setData((prev) => ({ ...prev, customFocus: value, improvementType: "custom" }));
    } else {
      setValue("improvementType", "", { shouldDirty: true, shouldTouch: true, shouldValidate: true });
      setData((prev) => ({ ...prev, customFocus: "", improvementType: "" }));
    }
  };

  const handleImprovementTypeChange = (value) => {
    setValue("improvementType", value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setValue("customFocus", "", { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setData((prev) => ({ ...prev, improvementType: value, customFocus: "" }));
  };

  const handleClearPreset = () => {
    setValue("improvementType", "", { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setData((prev) => ({ ...prev, improvementType: "" }));
  };

  const handleClearCustom = () => {
    setValue("customFocus", "", { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setValue("improvementType", "", { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setData((prev) => ({ ...prev, customFocus: "", improvementType: "" }));
  };

  return (
    <FormProvider {...methods}>
      <div className="space-y-5 pt-4">
        <div>
          <h3 className="text-md font-semibold text-text-primary">Customize the AI Improvement</h3>
        </div>

        {/* <div className="space-y-1.5">
          <p className="text-sm font-medium text-text-primary">
            JD Mode <span className="text-red-400">*</span>
          </p>
          <RHFSelect
            name="jdMode"
            placeholder="Select JD Mode"
            options={[
              { label: "Improve with AI", value: "improve" },
              { label: "Extract as it is", value: "extract" },
            ]}
          />
          <p className="text-xs text-text-secondary">
            You can either improve the JD or extract it as it is.
          </p>
        </div> */}

        {jdMode === "extract" && (
          <p className="text-[11px] leading-relaxed text-text-secondary rounded-md border border-border-primary bg-background-icon/20 px-3 py-2">
            AI will extract your JD exactly as written into the form fields below - not a single word will be added, removed, or changed.
          </p>
        )}

        {jdMode === "improve" && (
          <>
            <div className="space-y-3">
              {!hasPresetSelected && !hasCustomText && (
                <p className="text-xs text-text-secondary">
                  Select an Improvement Type or write your own Custom Prompt. Only one option can be active at a time. <span className="text-red-400">*</span>
                </p>
              )}

              <div
                className="space-y-1.5"
                ref={firstMissingField === "focusOption" ? scrollRef : undefined}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-text-primary">Improvement Type {!hasCustomText && <span className="text-red-400">*</span>}</p>
                  {hasPresetSelected && (
                    <Button
                      onClick={handleClearPreset}
                      className="flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium border border-red-500/60 text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      x Clear
                    </Button>
                  )}
                </div>

                {hasPresetSelected && (
                  <div className="flex items-start gap-2 rounded-md border border-blue-500/25 bg-blue-500/10 px-3 py-2">
                    <div className="mt-[1px] shrink-0 text-blue-400">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="block">
                        <path d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Zm8-3a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5A.75.75 0 0 1 8 5Zm0 6a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z" fill="currentColor" />
                      </svg>
                    </div>
                    <p className="text-[11px] leading-relaxed text-text-blue">Improvement Type selected. Clear it if you want to write your own custom prompt.</p>
                  </div>
                )}

                {showDropdown && (
                  <RHFSelect
                    name="improvementType"
                    placeholder="None"
                    onValueChange={handleImprovementTypeChange}
                    options={[
                      { label: "Complete Job Description Rewrite", value: "complete_rewrite" },
                      { label: "Skills & Requirements Analysis", value: "skills_analysis" },
                      { label: "Job Title Optimization", value: "title_optimization" },
                      { label: "Attract Better Candidates", value: "attract_candidates" },
                      { label: "Diversity & Inclusion Enhancement", value: "diversity_inclusion" },
                      { label: "Compensation & Benefits Review", value: "compensation_review" },
                    ]}
                  />
                )}
              </div>

              {!hasPresetSelected && !hasCustomText && (
                <div className="flex items-center gap-2 pt-3.5">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-[13px] font-medium text-text-secondary">OR</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              )}

              {showCustomBox && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-text-primary">Custom Prompt {!hasPresetSelected && <span className="text-red-400">*</span>}</p>
                    {hasCustomText && (
                      <Button
                        onClick={handleClearCustom}
                        className="flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium border border-red-500/60 text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        x Clear
                      </Button>
                    )}
                  </div>

                  {hasCustomText && (
                    <div className="flex items-start gap-2 rounded-md border border-blue-500/25 bg-blue-500/10 px-3 py-2">
                      <div className="mt-[1px] shrink-0 text-blue-400">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="block">
                          <path d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Zm8-3a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5A.75.75 0 0 1 8 5Zm0 6a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z" fill="currentColor" />
                        </svg>
                      </div>
                      <p className="text-[11px] leading-relaxed text-blue-300">Custom prompt added. Clear it if you want to select an Improvement Type.</p>
                    </div>
                  )}

                  <RHFTextArea
                    name="customFocus"
                    placeholder="Write your own improvement instructions..."
                    {...customFocusField}
                    onChange={handleCustomChange}
                    className="min-h-[80px]"
                  />
                </div>
              )}
            </div>

            <div
              className="space-y-2"
              ref={firstMissingField === "responseDepth" ? scrollRef : undefined}
            >
              <p className="text-sm font-medium text-text-primary">
                Response Depth <span className="text-red-400">*</span>
              </p>
              <RHFSelect
                name="responseDepth"
                placeholder="Select Response Depth"
                options={[
                  { label: "Minimal Response", value: "Minimal Response" },
                  { label: "Standard Response", value: "Standard Response" },
                  { label: "Detailed Response", value: "Detailed Response" },
                ]}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div
                className="space-y-2"
                ref={firstMissingField === "enhancement_tone" ? scrollRef : undefined}
              >
                <p className="text-sm font-medium text-text-primary">
                  Enhancement Tone <span className="text-red-400">*</span>
                </p>
                <RHFSelect
                  name="enhancement_tone"
                  placeholder="Select Desired Tone"
                  options={[
                    { label: "Professional", value: "Professional" },
                    { label: "Friendly", value: "Friendly" },
                    { label: "Inclusive", value: "Inclusive" },
                    { label: "Executive", value: "Executive" },
                  ]}
                />
              </div>

              <div
                className="space-y-2"
                ref={firstMissingField === "improvement_flexibility" ? scrollRef : undefined}
              >
                <p className="text-sm font-medium text-text-primary">
                  Improvement Flexibility <span className="text-red-400">*</span>
                </p>
                <RHFSelect
                  name="improvement_flexibility"
                  placeholder="Select Improvement Flexibility"
                  options={[
                    { label: "Strict", value: "Strict" },
                    { label: "Balanced", value: "Balanced" },
                    { label: "Open Ended", value: "Flexible" },
                  ]}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </FormProvider>
  );
};

export default Step2;
